/**
 * LoR Preview Dialog
 *
 * Presentational dialog for displaying a generated LoR preview. Handles three
 * states: loading, loaded with URL, and empty/error.
 */
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading: boolean;
  previewUrl: string | null;
  error?: unknown;
  onDownloadDocx?: () => void;
  onDownloadPdf?: () => void;
  disableActions?: boolean;
  showDocxButton?: boolean;
  docxLabel?: string;
  pdfLabel?: string;
  appendOptions?: Array<{ id: string; label: string }>;
  selectedAppendId?: string | null;
  onChangeAppendId?: (id: string | null) => void;
};

/**
 * LorPreviewDialog
 *
 * Right-side drawer dialog displaying a PDF preview of a Letter of
 * Representation. Handles loading and empty/error states, and accepts an
 * externally generated preview URL (typically from `useLorPreview`).
 *
 * Accessibility: the embedded iframe is labeled via title attribute; overall
 * dialog content uses a visually hidden heading for screen readers.
 */
export const LorPreviewDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  isLoading,
  previewUrl,
  error,
  onDownloadDocx,
  onDownloadPdf,
  disableActions,
  showDocxButton = true,
  docxLabel = 'Download DOCX',
  pdfLabel = 'Download PDF',
  appendOptions,
  selectedAppendId,
  onChangeAppendId,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[4%] translate-y-0 data-[state=open]:slide-in-from-top-[2%] max-w-[min(95vw,960px)] w-[min(95vw,960px)] max-h-[92vh] overflow-y-auto bg-white shadow-lg p-0 gap-0 block">
        <DialogHeader className="flex items-start justify-between gap-4 border-b px-6 py-4">
          <div>
            <DialogTitle>Letter of Representation (Preview)</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {previewUrl
                ? 'Review the generated Letter of Representation.'
                : 'Rendering preview…'}
            </DialogDescription>
          </div>
          <DialogClose className="rounded-md bg-slate-100 px-3 py-1 text-sm font-medium">
            Close
          </DialogClose>
        </DialogHeader>
        <div className="border-b px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {appendOptions && appendOptions.length > 0 && (
              <div className="flex items-center gap-2 min-w-0">
                <span
                  id="append-pdf-label"
                  className="text-xs text-slate-600 whitespace-nowrap"
                >
                  Append PDF
                </span>
                <Select
                  value={selectedAppendId ?? '__none__'}
                  onValueChange={(v) =>
                    onChangeAppendId?.(v === '__none__' ? null : v)
                  }
                >
                  <SelectTrigger
                    id="append-pdf-select"
                    aria-labelledby="append-pdf-label"
                    className="h-8 w-[220px]"
                  >
                    <SelectValue placeholder="None (download LoR only)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-72 overflow-auto">
                    <SelectItem value="__none__">None</SelectItem>
                    {appendOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showDocxButton && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onDownloadDocx}
                disabled={Boolean(disableActions || !onDownloadDocx)}
                aria-label="Download as DOCX"
              >
                {docxLabel}
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={onDownloadPdf}
              disabled={Boolean(disableActions)}
              aria-label="Download as PDF"
            >
              {pdfLabel}
            </Button>
          </div>
        </div>
        {isLoading && (
          <div
            className="flex items-center justify-center h-[70vh] text-slate-500 text-sm"
            aria-live="polite"
          >
            Loading preview…
          </div>
        )}
        {!isLoading && previewUrl && (
          <iframe
            title="LoR Preview"
            src={`${previewUrl}#navpanes=0`}
            className="w-full h-[70vh] border-0"
          />
        )}
        {!isLoading && !previewUrl && (
          <div
            className="flex items-center justify-center h-[70vh] text-slate-500 text-sm"
            aria-live="polite"
          >
            {error ? 'Preview failed to load.' : 'Preview unavailable.'}
          </div>
        )}
        <DialogFooter className="border-t px-6 py-3 text-right">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
